import classNames from "classnames";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { HighlightSearchTerm } from "components/DesignSystem/HighlightText";
import Table from "components/DesignSystem/Table/V2/Table";
import { LoadingIcon } from "components/icons/LoadingIcon";
import { Docs } from "components/icons/Navbar/Docs";
import { FOLDER_TYPE, LOCKED_FOLDER } from "constants/documents";
import { useQuery } from "hooks/useQuery";
import React, { useContext } from "react";
import { useDrag, useDrop } from "react-dnd";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { FileIcon } from "components/design/FileIcon";
import { formatDate } from "utils/formatDate";
import ToolTip from "../../design/toolTip";
import { DocumentContext } from "../documentContext";
import FileFolderActionDropDown from "../fileFolderActionDropdown";
import { getFileType } from "utils/getFileType";
import { getItemCountText } from "../folderAndFileSection";
import { FOLDER } from "constants/fileTypes";
import { DocumentTagCombobox } from "components/DocumentTags/DocumentTagCombobox";
import { DocumentTagStatus } from "components/DocumentTags/DocumentTagStatus";
import { useConfirmDocumentTagsMutation } from "store/apis/documentTagReview";
import { useGetSupportingDocumentTagsQuery } from "store/apis/documents";
import { useToast } from "hooks/useToast";
import { useRoleBasedView } from "hooks/useRoleBasedView";

const DraggableRow = ({
  name,
  type,
  onDrop,
  uuid,
  isChecked,
  isFileAlreadySelected,
  onClick,
  ...rest
}) => {
  const handleDrop = ({ fileId }) => {
    onDrop({
      folderId: uuid,
      fileId,
    });
  };

  const [{ isOver }, drop] = useDrop(() => ({
    accept: "file",
    drop: handleDrop,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const [_, drag] = useDrag(
    () => ({
      type: "file",
      item: { fileId: uuid },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [uuid]
  );

  const ref = type === "FILE" ? drag : drop;

  return (
    <tr
      onClick={onClick}
      ref={ref}
      className={classNames(
        "t-cursor-pointer t-border-neutral-0 t-border-b t-border-0 t-text-body t-px-3 t-border-solid",
        {
          "t-bg-surface-purple": isChecked,
          "hover:t-bg-surface-lighter-grey": !isChecked,
          "t-border-purple !t-bg-purple-0": isOver,
          "!t-cursor-no-drop": isFileAlreadySelected,
        }
      )}
      {...rest}
    />
  );
};

export const ListView = ({
  userGroupId,
  entityFileAndFolder,
  groupId,
  setLoad,
  setNewFolderName,
  doubleClick,
  setMovedFolderName,
  groupDocID,
  ParentId,
  moveFileOnDrop,
  handleCheckBox,
  onSelectAll,
  currentFolder,
  refreshFoldersSilently,
}) => {
  const query = useQuery();
  const searchTerm = query.get("search");
  const {
    fetchMoreData,
    handleFolderClick,
    selectedRecentDocs,
    isSearchLoading,
    setEntityFileAndFolder,
  } = useContext(DocumentContext);
  const { alertToast } = useToast();
  const { isCpa, isAdmin } = useRoleBasedView();
  const canManageTags =
    (isCpa || isAdmin) &&
    Boolean(currentFolder?.is_supporting_documents_folder);
  const [confirmDocumentTags] = useConfirmDocumentTagsMutation();
  const { data: tagOptions = [] } = useGetSupportingDocumentTagsQuery(
    { groupId },
    { skip: !groupId }
  );

  const setDocumentTagsOptimistically = (documentId, tagUuids) => {
    const nextTags = tagUuids
      .map((uuid) => tagOptions.find((tag) => tag.uuid === uuid))
      .filter(Boolean);

    setEntityFileAndFolder((prev) =>
      prev.map((item) =>
        item.uuid === documentId ? { ...item, document_tags: nextTags } : item
      )
    );
  };

  const onDocumentTagsChange = async (documentId, tagUuids) => {
    setDocumentTagsOptimistically(documentId, tagUuids);

    try {
      await confirmDocumentTags({
        groupId,
        documents: [{ document_id: documentId, tag_ids: tagUuids }],
      }).unwrap();
      refreshFoldersSilently();
    } catch (err) {
      refreshFoldersSilently();
      alertToast({
        message: err?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  const folderAndFileData = React.useMemo(
    () => entityFileAndFolder,
    [entityFileAndFolder]
  );

  const createColumn = createColumnHelper();

  const location = searchTerm
    ? [
        createColumn.accessor("location", {
          size: 15,
          header: "Location",
          cell: (info) => {
            const { parent_name, parent_uuid } = info.row.original;

            return (
              <div
                className="t-flex t-items-center t-gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                  handleFolderClick(parent_uuid);
                }}
              >
                <Docs size="16" />
                <span className="t-max-w-20 t-truncate t-text-body-sm t-text-purple hover:!t-text-purple hover:!t-underline">
                  {parent_name}
                </span>
              </div>
            );
          },
        }),
      ]
    : [];

  const columns = React.useMemo(
    () => [
      createColumn.display({
        id: "select",
        size: 1,
        header: () => (
          <Checkbox
            checked={
              entityFileAndFolder.length > 0 &&
              entityFileAndFolder.every((item) => item.isChecked)
            }
            onChange={onSelectAll}
          />
        ),
        cell: ({ row }) => {
          const { isChecked, uuid } = row.original || {};
          const isFileAlreadySelected = selectedRecentDocs.some(
            ({ uuid: selectedFileId }) => selectedFileId === uuid
          );

          return (
            <ConditionalToolTip
              condition={
                isFileAlreadySelected && "This file has selected already"
              }
            >
              <span>
                <Checkbox
                  checked={isChecked}
                  onChange={(e) => handleCheckBox(uuid, e)}
                  onClick={(e) => e.stopPropagation()}
                  disabled={isFileAlreadySelected}
                />
              </span>
            </ConditionalToolTip>
          );
        },
      }),

      createColumn.accessor("name", {
        id: "name",
        size: canManageTags ? 25 : 40,
        header: "Name",
        cell: (info) => {
          const {
            name,
            file_type,
            is_locked,
            type,
            is_private,
            item_count,
            file_size,
          } = info.row.original;

          let fileType = getFileType(file_type, type, is_locked, is_private);

          return (
            <div className="listNameIconContainer nameContainer">
              <span>
                <FileIcon
                  fileType={fileType}
                  size="40"
                  className="t-h-10 t-w-10"
                />
              </span>{" "}
              <div
                className={classNames("eleContent nameContainer", {
                  "t-w-32": canManageTags,
                })}
              >
                <ToolTip text={name}>
                  <span>
                    <HighlightSearchTerm text={name} searchTerm={searchTerm} />
                  </span>
                </ToolTip>
                <br />
                {fileType.includes(FOLDER) ? (
                  <span className="t-italic t-text-neutral-40 t-text-caption">
                    {getItemCountText(item_count)}
                  </span>
                ) : file_size ? (
                  <span className="t-text-neutral-40 t-text-caption">
                    {file_size}MB
                  </span>
                ) : null}
              </div>
            </div>
          );
        },
      }),
      createColumn.accessor("modified", {
        size: canManageTags ? 21 : 15,
        header: "Created On",
        cell: (info) => {
          const { created_at } = info.row.original;

          return (
            <span
              className="eleContent listNameIconContainer"
              content={created_at ? formatDate(created_at) : "-"}
            />
          );
        },
      }),
      createColumn.accessor("owner", {
        size: canManageTags ? 21 : 20,
        header: "Owner",
        cell: (info) => {
          const { owner } = info.row.original;

          return (
            <span className="eleContent listNameIconContainer">
              <HighlightSearchTerm
                text={owner || "-"}
                searchTerm={searchTerm}
              />
            </span>
          );
        },
      }),

      ...(canManageTags
        ? [
            createColumn.accessor("document_tags", {
              size: 20,
              header: "Tags",
              cell: (info) => {
                const { type, uuid, document_tags, tag_status } =
                  info.row.original;

                const tags = document_tags || [];

                if (type === "FOLDER") {
                  return (
                    <span
                      className="eleContent listNameIconContainer"
                      content="-"
                    />
                  );
                }

                return (
                  <div
                    className="t-flex t-items-center t-gap-1.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="t-w-52">
                      <DocumentTagCombobox
                        groupId={groupId}
                        selectedUuids={tags.map((tag) => tag.uuid)}
                        onChange={(tagUuids) =>
                          onDocumentTagsChange(uuid, tagUuids)
                        }
                      />
                    </div>
                    {tags.length > 0 && (
                      <DocumentTagStatus
                        groupId={groupId}
                        documentId={uuid}
                        tagStatus={tag_status}
                        tags={tags}
                        onConfirmed={refreshFoldersSilently}
                      />
                    )}
                  </div>
                );
              },
            }),
          ]
        : []),

      createColumn.accessor("size", {
        size: canManageTags ? 6 : 10,
        header: "Type",
        cell: (info) => {
          const { type, tags } = info.row.original;
          if (type === "FOLDER")
            return (
              <span className="eleContent listNameIconContainer" content="-" />
            );
          const label =
            tags?.length > 0
              ? tags
                  .map((t) => t.charAt(0) + t.slice(1).toLowerCase())
                  .join(", ")
              : "-";

          return (
            <ConditionalToolTip condition={canManageTags && label}>
              <span className="eleContent listNameIconContainer t-text-text-60 t-text-body-sm">
                <span
                  className={classNames({
                    "t-block t-w-40 t-truncate t-text-left": canManageTags,
                  })}
                >
                  {label}
                </span>
              </span>
            </ConditionalToolTip>
          );
        },
      }),

      ...location,
      createColumn.accessor("action", {
        size: 5,
        header: "",
        cell: (info) => {
          const {
            file_type,
            type,
            is_locked,
            is_previewable,
            uuid,
            name,
            parent_uuid,
            is_private,
          } = info.row.original;
          const fileType =
            is_locked && type === FOLDER_TYPE ? LOCKED_FOLDER : file_type;

          return (
            <FileFolderActionDropDown
              fileType={fileType}
              userGroupId={userGroupId}
              itemType={type}
              isPreviewable={is_previewable}
              folderId={uuid}
              groupId={groupId}
              setLoad={setLoad}
              folderName={name}
              setNewFolderName={setNewFolderName}
              folderDrowdowns={false}
              setMovedFolderName={setMovedFolderName}
              groupDocID={groupDocID}
              ParentId={ParentId}
              parentUuid={parent_uuid}
              isLocked={is_locked}
              listView
              isPrivate={is_private}
            />
          );
        },
      }),
    ],
    [entityFileAndFolder, selectedRecentDocs, onSelectAll, canManageTags]
  );

  const folderFileClick = (
    { file_type, uuid, name, type, parent_uuid, is_locked, is_previewable },
    e
  ) => {
    const isFileAlreadySelected = selectedRecentDocs.some(
      ({ uuid: selectedFileId }) => selectedFileId === uuid
    );
    if (isFileAlreadySelected) {
      e.stopPropagation();
    } else {
      doubleClick({
        e,
        fileType: file_type,
        uuid,
        name,
        type,
        parent_uuid,
        is_locked,
        isRecentDocument: false,
      });
    }
  };

  const table = useReactTable({
    data: folderAndFileData || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
  });

  const Wrapper = searchTerm ? "div" : React.Fragment;

  return (
    <Wrapper
      className="t-overflow-auto t-sticky t-top-0 t-h-[calc(100vh-260px)]"
      id="scrollableList"
    >
      <InfiniteScroll
        dataLength={entityFileAndFolder.length}
        next={fetchMoreData}
        hasMore={true}
        scrollableTarget="scrollableList"
        loader={
          isSearchLoading ? (
            <div className="t-flex t-justify-center t-items-center t-w-full t-gap-2 t-p-2">
              <span className="t-flex t-origin-center t-animate-spin">
                <LoadingIcon />
              </span>
              Loading...
            </div>
          ) : null
        }
      >
        <Table.Container onContextMenu={(e) => e.preventDefault()}>
          <Table.Content>
            <Table.Head>
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.Row key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Table.HeadCell
                      key={header.id}
                      style={{ width: `${header.getSize()}%` }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </Table.HeadCell>
                  ))}
                </Table.Row>
              ))}
            </Table.Head>
            <Table.Body>
              {table.getRowModel().rows.map((row) => {
                const { type, uuid, name, isChecked } = row.original;
                const isFileAlreadySelected = selectedRecentDocs.some(
                  ({ uuid: selectedFileId }) => selectedFileId === uuid
                );

                return (
                  <DraggableRow
                    name={name}
                    onDrop={moveFileOnDrop}
                    isChecked={isChecked}
                    isFileAlreadySelected={isFileAlreadySelected}
                    type={type}
                    uuid={uuid}
                    key={row.id}
                    onClick={(e) => {
                      folderFileClick(row.original, e);
                    }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <Table.Cell
                          key={cell.id}
                          style={{
                            width: `${cell.column.getSize()}%`,
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.Cell>
                      );
                    })}
                  </DraggableRow>
                );
              })}
            </Table.Body>
          </Table.Content>
        </Table.Container>
      </InfiniteScroll>
    </Wrapper>
  );
};
